import { computed, effect, inject, ResourceRef, Signal, signal, untracked } from '@angular/core'
import { classifyHttpFailure, HttpFailureKind, isRecoverableFailure } from '../util/http-failure.util'
import { ConnectionService } from './connection.service'

export interface ResourceFailure {
  /** Die anzuzeigende Fehlerart — `null`, wenn nichts fehlgeschlagen ist. */
  readonly kind: Signal<HttpFailureKind | null>
  /** Ein Neuversuch laeuft gerade. */
  readonly isRetrying: Signal<boolean>
  /** Laedt genau die fehlgeschlagenen Resourcen neu. */
  retry(): void
}

/**
 * Buendelt den Fehlerzustand einer oder mehrerer Resourcen: Ursache merken, Wiederholen,
 * automatisch nachladen, sobald SaniUp wieder antwortet.
 *
 * Warum die Fehlerart **gemerkt** und nicht abgeleitet wird: `reload()` setzt eine
 * fehlgeschlagene Resource auf `loading` zurueck und raeumt dabei ihren Fehler weg. Abgeleitet
 * waere der Fehlerzustand fuer die Dauer jedes Versuchs verschwunden und danach wieder da — ein
 * Flackern, das nach Defekt aussieht statt nach „wird gerade nochmal versucht".
 *
 * Muss in einem Injection-Context aufgerufen werden (Feld-Initialisierung einer Komponente).
 */
export function trackResourceFailure(resources: () => ResourceRef<unknown>[]): ResourceFailure {
  const connectionService = inject(ConnectionService)
  const kind = signal<HttpFailureKind | null>(null)

  const anyIsLoading = computed(() => resources().some((r) => r.status() === 'loading' || r.status() === 'reloading'))

  effect(() => {
    const current = resources()
    const failed = current.find((r) => r.status() === 'error')
    if (failed) {
      kind.set(classifyHttpFailure(failed.error()))
    } else if (current.every((r) => r.hasValue())) {
      kind.set(null)
    }
  })

  const retry = () =>
    resources()
      .filter((r) => r.status() === 'error')
      .forEach((r) => r.reload())

  // Die Selbstheilung nudgt nur — welche Abfragen betroffen sind, weiss allein die Ansicht.
  // Nachgeladen wird deshalb hier, und nur bei einem Fehler, den ein zweiter Versuch loesen kann.
  effect(() => {
    const tick = connectionService.retryTick()
    untracked(() => {
      const current = kind()
      if (tick > 0 && current !== null && isRecoverableFailure(current) && !anyIsLoading()) {
        retry()
      }
    })
  })

  return {
    kind: kind.asReadonly(),
    isRetrying: computed(() => kind() !== null && anyIsLoading()),
    retry,
  }
}
